// "What this means": a plain-language reading of a request the log shows,
// for the known cases a support reference usually turns out to be (owner
// 2026-10-13). The Request log drawer shows it above the raw Error.
//
// Each rule names the service, the route, the status and what the request
// recorded: the response body's machine-readable `reason` / `code` (what
// newer builds answer), else a pattern on the stored error text (rows
// written before a service answered a reason). The first rule that matches
// wins, so put the narrow ones first. A rule never guesses: when nothing
// matches, the drawer shows the raw request only.
//
// It lives here, not in content: the wording is the admin's (like
// origin.js and the audit entry words), it reads only what the detail
// already answers, it covers rows already in the log, and a new case ships
// with the admin alone.

// The response body as an object, or null (a body is whatever redaction
// kept: not always JSON).
const bodyOf = d => {
  const raw = d && d.responseBody
  if (!raw || typeof raw !== 'string') return null
  try {
    const v = JSON.parse(raw)
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null
  } catch (e) {
    return null
  }
}

const textOf = (body, key) => (body && typeof body[key] === 'string' ? body[key] : '')

// One step of an SSO sign-in that carries the one-time sign-in token in its
// path (auth /auth-provider/:id/<step>; :id is the token there).
const SIGN_IN_STEP = /^\/api\/v1\/auth-provider\/:id\/(organization|details|invites|join-code|register|link|link-confirmation|signin)(\/|$)/

const SIGN_IN_LINK = {
  used: 'The sign-in link was already used',
  expired: 'The sign-in link had expired',
  either: 'The sign-in link was already used or had expired'
}

const RULES = [
  {
    // A tab left open on an SSO sign-in page, reloaded or used later: its
    // token was redeemed (by the app that started the sign-in) or is past
    // its 15 minutes, and the row is gone. Auth answers 404 with reason
    // sign_in_link_used / sign_in_link_expired; older rows only have the
    // error text "passive-login repo get err ... record not found".
    id: 'sso_sign_in_link',
    match: (d, body) => {
      if (d.service !== 'auth' || !SIGN_IN_STEP.test(d.route || '')) return null
      const reason = textOf(body, 'reason')
      if (reason === 'sign_in_link_used') return 'used'
      if (reason === 'sign_in_link_expired') return 'expired'
      if (Number(d.status) === 404 && /passive-login repo get err/i.test(d.error || '') && /record not found/i.test(d.error || '')) return 'either'
      return null
    },
    tone: 'info',
    title: variant => SIGN_IN_LINK[variant],
    lines: variant => [
      'This request was one step of a single sign-on (SSO) sign-in. Each sign-in gets a one-time sign-in link (a token) that works once and for 15 minutes.',
      variant === 'expired'
        ? 'This link was used more than 15 minutes after the sign-in started, so it had expired.'
        : variant === 'used'
          ? 'This link had already been used, usually by the app that started the sign-in, which uses it as soon as the person finishes signing in.'
          : 'This link had already been used (usually by the app that started the sign-in, as soon as the person finished signing in) or had expired 15 minutes after the sign-in started.',
      'Nothing is broken. A browser tab left open on an old sign-in page shows an error like this when it is reloaded or used later. The person just signs in again.'
    ]
  },
  {
    // SSO link confirmation (owner decision Option A, 2026-10-16): a public
    // sign-in button (Google, Microsoft, Facebook...) met an existing
    // account it isn't connected to, so auth holds the sign-in until the
    // person proves the account is theirs, and refuses every later step of
    // it with 403 reason link_confirmation_required.
    id: 'sso_link_confirmation',
    match: (d, body) =>
      d.service === 'auth' && /^\/api\/v1\/auth-provider\/:id\//.test(d.route || '') && textOf(body, 'reason') === 'link_confirmation_required' ? 'held' : null,
    tone: 'info',
    title: () => 'The sign-in was waiting for the person to confirm the account',
    lines: () => [
      "The sign-in is waiting for the person to confirm the account is theirs: a public sign-in button met an existing account it isn't connected to.",
      'Nothing is broken. The person confirms once, with the account\'s password, a code emailed to the account, or a sign-in method the account already has, and the sign-in carries on.',
      'If it repeats on one account and is never confirmed, someone may be trying to get in. Findings lists each one as "Asked to confirm a new sign-in method".'
    ]
  },
  {
    // A session renewal (auth /auth-refresh/:refreshToken[/recreate])
    // refused for a reason the session policy gives (auth NewErrorReason).
    id: 'session_ended',
    match: (d, body) => {
      if (d.service !== 'auth' || Number(d.status) !== 401 || !/^\/api\/v1\/auth-refresh\/:refreshToken(\/recreate)?$/.test(d.route || '')) return null
      const reason = textOf(body, 'reason')
      return ['idle', 'max', 'expired', 'impersonation_limit'].includes(reason) ? reason : null
    },
    tone: 'info',
    title: reason => ({
      idle: 'The session ended because it was idle',
      max: 'The session reached its maximum length',
      expired: 'The session had already expired',
      impersonation_limit: 'The impersonation had ended'
    })[reason],
    lines: reason => [
      ({
        idle: 'The app tried to renew a session that had gone unused for longer than the idle limit, so it was signed out.',
        max: 'The app tried to renew a session older than the longest a session may last, so it was signed out.',
        expired: 'The app tried to renew a session whose sign-in had already expired (a laptop waking after days, an old tab).',
        impersonation_limit: 'An impersonation ends 2 hours after it started, and renewing it never extends it. This renewal came after that, or after the impersonation was ended.'
      })[reason],
      'Nothing is broken. The person (or the admin, for an impersonation) signs in again.'
    ]
  },
  {
    // core orggate: a token of a switched-off organization (a deletion in
    // progress) is refused on every route of every service.
    id: 'organization_unavailable',
    match: (d, body) => (Number(d.status) === 401 && textOf(body, 'code') === 'organization_unavailable' ? 'yes' : null),
    tone: 'info',
    title: () => 'The organization is switched off',
    lines: () => [
      'This request was made with a session of an organization that is switched off (it is being deleted), so every service refuses it.',
      'This is expected while the deletion is open. Restoring the organization lets its people sign in again.'
    ]
  },
  {
    // core requestlog: the client went away before the answer (status 499).
    id: 'client_went_away',
    match: d => (Number(d.status) === 499 ? 'yes' : null),
    tone: 'info',
    title: () => 'The app stopped waiting',
    lines: () => [
      'The app or browser stopped waiting before the server answered: the tab was closed or moved on, the request was cancelled, or the network dropped.',
      'This is not a server fault, and it is never reported as an error. A request that was slow before it was dropped shows its duration here.'
    ]
  }
]

// What the request means, or null: { id, tone, title, lines }.
export const explain = d => {
  if (!d) return null
  const body = bodyOf(d)
  for (const rule of RULES) {
    const variant = rule.match(d, body)
    if (variant) return { id: rule.id, tone: rule.tone, title: rule.title(variant), lines: rule.lines(variant) }
  }
  return null
}

export const EXPLANATION_IDS = RULES.map(r => r.id)
